/**
 * The evaluator (`specs/M2-formula.md` §6).
 *
 * The caller supplies one function — "what does `{ref}`, optionally `#Subfield`, evaluate to on
 * this item" — because resolving a column value is the *column registry's* job, not the
 * formula language's (CLAUDE.md rule 6). `packages/shared/src/columns/formula` builds that
 * resolver out of the sibling cells and their own definitions, which is where the one rule the
 * census pins down lives: **inside a formula a status is its label text, not its index**
 * `[for§5]`.
 *
 * Errors are values, not exceptions: an unresolvable column produces monday's own
 * *"A column named "X" doesn't exist in this board."* and that message propagates to the cell,
 * exactly as the sandbox capture shows.
 */
import { formulaError, isFormulaError, unknownColumnError, } from './ast.js';
import { toDate, toNumber, toText } from './coerce.js';
import { findFormulaFunction } from './functions.js';
import { FormulaSyntaxError, parseFormula } from './parser.js';
function compare(operator, left, right) {
    const leftNumber = toNumber(left);
    const rightNumber = toNumber(right);
    const bothNumeric = leftNumber !== null && rightNumber !== null;
    // A status compares as its **label text** `[for§5]`, so a string comparison is the fallback.
    const leftText = toText(left);
    const rightText = toText(right);
    switch (operator) {
        case '=':
            return bothNumeric ? leftNumber === rightNumber : leftText === rightText;
        case '<>':
        case '!=':
            return bothNumeric ? leftNumber !== rightNumber : leftText !== rightText;
        case '>':
            return bothNumeric ? leftNumber > rightNumber : leftText > rightText;
        case '>=':
            return bothNumeric ? leftNumber >= rightNumber : leftText >= rightText;
        case '<':
            return bothNumeric ? leftNumber < rightNumber : leftText < rightText;
        case '<=':
            return bothNumeric ? leftNumber <= rightNumber : leftText <= rightText;
        default:
            return formulaError('syntax', `unknown operator "${operator}"`);
    }
}
function arithmetic(operator, left, right) {
    if (operator === '+') {
        // `+` on two dates is meaningless; on a date and a number it shifts by days.
        const leftDate = toDate(left);
        const rightNumber = toNumber(right);
        if (leftDate !== null && toDate(right) === null && rightNumber !== null) {
            const shifted = new Date(Date.parse(`${leftDate.iso}T00:00:00Z`) + rightNumber * 86_400_000);
            return { kind: 'date', iso: shifted.toISOString().slice(0, 10) };
        }
    }
    const a = toNumber(left);
    const b = toNumber(right);
    // Two empty numeric cells add up to 0, which is what a spreadsheet does and what the
    // account's `{text5}+{lrg_weight__g_}` formula relies on (both cells are often empty).
    const x = a ?? 0;
    const y = b ?? 0;
    switch (operator) {
        case '+':
            return x + y;
        case '-':
            return x - y;
        case '*':
            return x * y;
        case '/':
            // Two of this account's formulas divide by a `numbers` cell that is empty on most rows
            // (`{numbers2}/{total_spent}`), so this is the common case, not the exotic one.
            return y === 0 ? formulaError('divide_by_zero', 'Division by zero') : x / y;
        default:
            return formulaError('syntax', `unknown operator "${operator}"`);
    }
}
export function evaluateNode(node, ctx) {
    switch (node.type) {
        case 'number':
            return node.value;
        case 'string':
            return node.value;
        case 'column': {
            const resolved = ctx.resolveColumn(node.ref, node.field);
            return resolved === undefined ? unknownColumnError(node.ref) : resolved;
        }
        case 'unary': {
            const operand = evaluateNode(node.operand, ctx);
            if (isFormulaError(operand))
                return operand;
            const numeric = toNumber(operand) ?? 0;
            return node.operator === '-' ? -numeric : numeric;
        }
        case 'binary': {
            const left = evaluateNode(node.left, ctx);
            if (isFormulaError(left))
                return left;
            const right = evaluateNode(node.right, ctx);
            if (isFormulaError(right))
                return right;
            if (node.operator === '&')
                return `${toText(left)}${toText(right)}`;
            if (['=', '<>', '!=', '>', '>=', '<', '<='].includes(node.operator)) {
                return compare(node.operator, left, right);
            }
            return arithmetic(node.operator, left, right);
        }
        case 'call': {
            const fn = findFormulaFunction(node.name);
            if (fn === undefined) {
                return formulaError('unknown_function', `${node.name} is not a supported function.`);
            }
            if (node.args.length < fn.minArgs || node.args.length > fn.maxArgs) {
                return formulaError('argument', `${fn.name} expects ${fn.signature}`);
            }
            const args = node.args.map((arg) => evaluateNode(arg, ctx));
            // `IF` must see an error argument (it may be in the branch not taken), so errors are
            // **not** short-circuited here; every other function checks its own.
            if (fn.name !== 'IF') {
                const failed = args.find(isFormulaError);
                if (failed !== undefined)
                    return failed;
            }
            return fn.call(args, { now: ctx.now ?? new Date(), timezone: ctx.timezone });
        }
        default:
            return formulaError('syntax', 'unsupported expression');
    }
}
/** Parses and evaluates in one call; an empty expression yields an empty cell `[for§5]`. */
export function evaluateFormula(source, ctx) {
    let node;
    try {
        node = parseFormula(source);
    }
    catch (error) {
        const message = error instanceof FormulaSyntaxError ? `The formula could not be parsed: ${error.message}` : String(error);
        return { value: formulaError('syntax', message), display: message, error: message };
    }
    if (node === null)
        return { value: null, display: '', error: null };
    const value = evaluateNode(node, ctx);
    if (isFormulaError(value))
        return { value, display: '', error: value.message };
    return { value, display: toText(value), error: null };
}
//# sourceMappingURL=evaluate.js.map